import { Stack } from "@carbon/react";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { object, string } from "zod";

import { TextAreaFormField } from "src/components/Form/TextAreaFormField";
import { TextInputFormField } from "src/components/Form/TextInputFormField";
import { ConfirmActionModal } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError } from "src/errors";
import { getRulesQuery } from "src/graphql/anti-fraud/queries/rules/GetRulesQuery.ts";
import { updateRuleMutation } from "src/graphql/anti-fraud/queries/rules/mutations/UpdateRuleMutation.ts";
import { usePerformMutation } from "src/graphql/utils";
import { useT } from "src/internationalization";
import { Rule } from "src/types/rule";

export type UpdateRuleModalProps = {
  rule: Rule;
  rules: Rule[];
  onClose: () => void;
};

type UpdateRulePayload = {
  name?: string | undefined;
  description?: string | undefined;
};

export const UpdateRuleModal: React.FC<UpdateRuleModalProps> = ({ rule, rules, onClose }) => {
  const { showSuccessToast } = useToast();
  const t = useT();
  const validationSchema = useSchema(rules, rule);

  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = useForm<UpdateRulePayload>({
    defaultValues: { name: rule.name, description: rule.description ?? "" },
    resolver: zodResolver(validationSchema),
  });

  const { loading, error, performMutation } = usePerformMutation(updateRuleMutation, {
    onSuccess: () => {
      showSuccessToast(t("page.rules.modal.update.toast.success.message"));
      onClose();
    },
    mutationOption: {
      awaitRefetchQueries: true,
      refetchQueries: [{ query: getRulesQuery }],
    },
  });

  const onSubmit: SubmitHandler<UpdateRulePayload> = async ({ name, description }) => {
    await performMutation({ id: rule.id, name: name ?? "", description: description ?? "" });
  };

  return (
    <ConfirmActionModal
      loading={loading}
      title={t("page.rules.modal.update.title", { name: rule?.name })}
      confirmButtonText={t("general.actions.confirm")}
      onConfirm={handleSubmit(onSubmit)}
      error={mapToModalError(error)}
      onClose={onClose}
      initialFocus="#name"
      confirmButtonDisabled={!isDirty}
    >
      <Stack gap={5}>
        <TextInputFormField
          id="name"
          label={t("general.form.name.label")}
          helperText={t("page.rules.modal.update.form.name.helper")}
          control={control}
          name="name"
        />
        <TextAreaFormField
          id="description"
          label={t("general.form.description.label")}
          helperText={t("page.rules.modal.update.form.description.helper")}
          control={control}
          rules={{ required: t("general.form.description.error.required") }}
          name="description"
        />
      </Stack>
    </ConfirmActionModal>
  );
};

const useSchema = (rules: Rule[], rule: Rule) => {
  const t = useT();

  return useMemo(() => {
    return object({
      name: string()
        .min(1, t("general.form.name.error.required"))
        .refine(
          (value) =>
            !rules.some(
              (existingRule) => existingRule.name === value && existingRule.id !== rule.id
            ),
          {
            message: t("general.form.name.error.unique"),
          }
        ),
      description: string().min(1, t("general.form.description.error.required")),
    });
  }, [t, rule, rules]);
};
